import FormHelperText from '@mui/material/FormHelperText';
import ListItem from '@mui/material/ListItem';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { colorPalette } from '@repo/mui-config/color-palette';
import type { Children } from '@repo/shared/types';
import type { LoanResponseConfigParametersNames } from '@repo/api/endpoints/merchant';
import { useIsMobile } from '@repo/mui-config/hooks';

import type { LoanFormInputFields } from '@/components/loan-form/schema/loan-form.schema';
import { isPeselNumberError } from '@/components/loan-form/helpers/is-pesel-number-error';

interface DetailWrapper extends Children {
  isEven: boolean;
  configParameterName: LoanResponseConfigParametersNames;
}

export function DetailWrapper({ configParameterName, isEven, children }: DetailWrapper) {
  const { t } = useTranslation();
  const context = useFormContext<LoanFormInputFields>();
  const isMobile = useIsMobile();

  const isFieldError = Boolean(context.formState.errors[configParameterName]);
  const isPeselError =
    isPeselNumberError(context.getValues('pesel')) && configParameterName === 'pesel';

  const displayBackgroundColor = () => {
    if (isFieldError || isPeselError) {
      return colorPalette.error.light;
    }

    if (isEven) {
      return colorPalette.cloud.light;
    }

    return undefined;
  };

  return (
    <ListItem
      data-testid={`list-item-${configParameterName}`}
      sx={{
        p: '10px 24px',
        borderRadius: '10px',
        backgroundColor: displayBackgroundColor(),
      }}
    >
      <Grid
        alignItems={isMobile ? 'flex-start' : 'center'}
        container
        flexDirection={isMobile ? 'column' : 'row'}
        marginBlock={{ xs: '0px', lg: '10px', xl: '0px' }}
        sx={{ width: '100%' }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="textField">
              {t(`loanFormPage.borrowerDetails.form.fields.${configParameterName}`)}
            </Typography>
            <FormHelperText data-testid={`detail-field-error-${configParameterName}`} error>
              {context.formState.errors[configParameterName]?.message}
            </FormHelperText>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>{children}</Grid>
      </Grid>
    </ListItem>
  );
}
